import { createElement, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import InDevelopmentPage from "../pages/public/InDevelopmentPage";
import EnquireFormPage from "../pages/public/EnquireFormPage";
import EnquireConfirmationPage from "../pages/public/EnquireConfirmationPage";

/**
 * Public route registry (Feature #25438).
 *
 * A screen registers itself by adding `src/pages/public/<screen>/route.tsx` whose default export is a
 * PublicRoute. The registry collects them with import.meta.glob, so a screen Story never edits App.tsx.
 * A module wins over a built-in or planned entry with the same path. Path "*" is the catch-all
 * (the not-found screen); until one is registered, unknown paths redirect to "/".
 *
 * Route modules may call notFoundElement() — but only while rendering, never at module top level
 * (the registry imports the modules eagerly, so a top-level call would run before it is initialised).
 */
export interface PublicRoute {
  /** Absolute path, e.g. "/", "/products/:slug" or "*". */
  path: string;
  element: ReactNode;
  /** Unique document title (BR-WEB-004). */
  title: string;
  /** Meta description (BR-WEB-004). */
  description: string;
}

export interface RouteModule {
  default: PublicRoute;
}

export type RouteKind = "module" | "existing" | "planned";

export interface RouteEntry extends PublicRoute {
  kind: RouteKind;
}

/**
 * The padded frame the pre-registry layout gave every screen (`mx-auto max-w-5xl px-4 py-8` on <main>).
 * The site layout's <main> is full-bleed, so the existing enquiry screens, which have no padding of their
 * own, keep this frame here until their restyle Stories replace them with route modules.
 */
export function LegacyFrame({ children }: { children?: ReactNode }) {
  return createElement("div", { className: "mx-auto w-full max-w-5xl px-4 py-8", "data-testid": "legacy-frame" }, children);
}

/** The F1.5-2 enquiry screens that predate the registry (restyled by their own Stories). */
export const EXISTING_ROUTES: readonly PublicRoute[] = [
  {
    path: "/enquire",
    element: createElement(LegacyFrame, null, createElement(EnquireFormPage)),
    title: "Get a free quote | Greatway Patios",
    description:
      "Tell us about your project and request a free measure and quote from Greatway Patios. It takes about two minutes, and there’s no account to create.",
  },
  {
    path: "/enquire/confirmation",
    element: createElement(LegacyFrame, null, createElement(EnquireConfirmationPage)),
    title: "Thanks — we've received your enquiry | Greatway Patios",
    description:
      "A local rep will review your enquiry and get back to you within two business days.",
  },
];

interface PlannedRoute {
  path: string;
  name: string;
  title: string;
  description: string;
}

/**
 * Approved public screens not yet built. Each renders the labelled In-Development placeholder until
 * its route module lands. Titles/descriptions are the legacy ones where the page existed (baseline § 3,
 * typos fixed) and the prototype's otherwise.
 */
export const PLANNED_ROUTES: readonly PlannedRoute[] = [
  {
    path: "/products",
    name: "Our products",
    title: "Toowoomba Outdoor Living Products including Patios, Decks, Carports and Vergolas | Greatway Patios",
    description:
      "Check out our gallery of products to see our outdoor patio and outdoor room designs. With Toowoomba's only outdoor display centre you're sure to find the perfect solution for your home.",
  },
  {
    path: "/products/:slug",
    name: "Product details",
    title: "Products | Greatway Patios Toowoomba",
    description: "Outdoor living products supplied and installed by Greatway Patios, Toowoomba.",
  },
  {
    path: "/our-work",
    name: "Our work",
    title: "Our work — outdoor living projects across the Darling Downs | Greatway Patios",
    description:
      "Patios, decks, opening roofs and screens we’ve designed and built for homes across Toowoomba and the Darling Downs.",
  },
  {
    path: "/about",
    name: "About",
    title: "Our Services, team and awards set us apart as Toowoomba's leading Outdoor Living products experts | Greatway Patios",
    description:
      "Throughout the years, the high-skilled and professional team of Greatway Patios has been designing and building award winning outdoor patios and rooms where families build lasting memories.",
  },
  {
    path: "/contact",
    name: "Contact",
    title: "Contact Greatway Patios | (07) 4636 1766 | 396 South Street",
    description:
      "Call Greatway Patios on (07) 4636 1766 or by visiting us at 396 South Street, Toowoomba, Queensland 4350",
  },
];

export const ROUTE_MODULES = import.meta.glob<RouteModule>("../pages/public/*/route.tsx", { eager: true });

const CATCH_ALL = "*";

/** Every concrete route, in order: registered modules, then existing screens, then planned placeholders. */
export function buildRouteTable(modules: Record<string, RouteModule>): RouteEntry[] {
  const registered: RouteEntry[] = Object.values(modules)
    .map((m) => m.default)
    .filter((r) => r.path !== CATCH_ALL)
    .map((r) => ({ ...r, kind: "module" as const }));
  const taken = new Set(registered.map((r) => r.path));
  const existing: RouteEntry[] = EXISTING_ROUTES.filter((r) => !taken.has(r.path)).map((r) => ({
    ...r,
    kind: "existing" as const,
  }));
  existing.forEach((r) => taken.add(r.path));
  const planned: RouteEntry[] = PLANNED_ROUTES.filter((r) => !taken.has(r.path)).map((r) => ({
    path: r.path,
    title: r.title,
    description: r.description,
    element: createElement(InDevelopmentPage, { name: r.name }),
    kind: "planned" as const,
  }));
  return [...registered, ...existing, ...planned];
}

/** The registered "*" module, if any. */
export function catchAllRoute(modules: Record<string, RouteModule> = ROUTE_MODULES): PublicRoute | undefined {
  return Object.values(modules)
    .map((m) => m.default)
    .find((r) => r.path === CATCH_ALL);
}

/**
 * What an unknown path renders: the registered not-found screen, else a redirect to "/".
 * Screens that must show not-found (e.g. an unknown product slug) render this.
 */
export function notFoundElement(modules: Record<string, RouteModule> = ROUTE_MODULES): ReactNode {
  return catchAllRoute(modules)?.element ?? createElement(Navigate, { to: "/", replace: true });
}

export const ROUTES: readonly RouteEntry[] = buildRouteTable(ROUTE_MODULES);
